import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/seo/seo-head";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { Plus, Edit, Trash2, DollarSign, ChevronLeft, ChevronRight, TrendingUp, AlertTriangle, Scale } from "lucide-react";
import { showToast } from "@/lib/toast";
import { Skeleton } from "@/components/shared/skeleton";
import { useCurrency } from "@/hooks/use-currency";
import { useLanguage } from "@/contexts/language-context";

interface Asset {
  id: string;
  category: string;
  value: number;
  date: string;
}

interface Debt {
  id: string;
  category: string;
  amount: number;
  originalAmount: number | null;
  interestRate: number | null;
  dueDate: string | null;
}

export function NetWorthPage() {
  const { formatCurrency } = useCurrency();
  const { t, language } = useLanguage();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [debts, setDebts] = useState<Debt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [showDebtForm, setShowDebtForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [editingDebt, setEditingDebt] = useState<Debt | null>(null);
  const [selectedDebtForPayment, setSelectedDebtForPayment] = useState<Debt | null>(null);
  
  // Pagination state
  const [assetPage, setAssetPage] = useState(1);
  const [debtPage, setDebtPage] = useState(1);
  const itemsPerPage = 10;

  // Asset form state
  const [assetForm, setAssetForm] = useState({
    category: "",
    value: "",
    date: new Date().toISOString().split("T")[0],
  });

  // Debt form state
  const [debtForm, setDebtForm] = useState({
    category: "",
    amount: "",
    interestRate: "",
    dueDate: "",
  });

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [assetsData, debtsData] = await Promise.all([
        api.get<{ assets: Asset[] }>("/assets").catch(() => ({ assets: [] })),
        api.get<{ debts: Debt[] }>("/debts").catch(() => ({ debts: [] })),
      ]);
      setAssets(assetsData.assets);
      setDebts(debtsData.debts);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      showToast.error(t("common.error"));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Paginated data
  const paginatedAssets = useMemo(() => {
    const start = (assetPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return assets.slice(start, end);
  }, [assets, assetPage, itemsPerPage]);
  
  const paginatedDebts = useMemo(() => {
    const start = (debtPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return debts.slice(start, end);
  }, [debts, debtPage, itemsPerPage]);
  
  const assetTotalPages = Math.ceil(assets.length / itemsPerPage);
  const debtTotalPages = Math.ceil(debts.length / itemsPerPage);

  const handleAssetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingAsset) {
        await api.put(`/assets/${editingAsset.id}`, {
          category: assetForm.category,
          value: parseFloat(assetForm.value),
          date: assetForm.date,
        });
      } else {
        await api.post("/assets", {
          category: assetForm.category,
          value: parseFloat(assetForm.value),
          date: assetForm.date,
        });
      }
      setShowAssetForm(false);
      setEditingAsset(null);
      setAssetForm({ category: "", value: "", date: new Date().toISOString().split("T")[0] });
      fetchData();
    } catch (error) {
      console.error("Failed to save asset:", error);
      showToast.error(error instanceof Error ? error.message : t("common.error"));
    }
  };

  const handleDebtSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingDebt) {
        await api.put(`/debts/${editingDebt.id}`, {
          category: debtForm.category,
          amount: parseFloat(debtForm.amount),
          interestRate: debtForm.interestRate ? parseFloat(debtForm.interestRate) : null,
          dueDate: debtForm.dueDate || null,
        });
      } else {
        await api.post("/debts", {
          category: debtForm.category,
          amount: parseFloat(debtForm.amount),
          interestRate: debtForm.interestRate ? parseFloat(debtForm.interestRate) : null,
          dueDate: debtForm.dueDate || null,
        });
      }
      setShowDebtForm(false);
      setEditingDebt(null);
      setDebtForm({ category: "", amount: "", interestRate: "", dueDate: "" });
      showToast.success(editingDebt ? t("settings.success") : t("settings.success"));
      setDebtPage(1);
      fetchData();
    } catch (error) {
      console.error("Failed to save debt:", error);
      showToast.error(error instanceof Error ? error.message : t("common.error"));
    }
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDebtForPayment) return;

    try {
      await api.post(`/debts/${selectedDebtForPayment.id}/payment`, {
        amount: parseFloat(paymentForm.amount),
      });
      setShowPaymentForm(false);
      setSelectedDebtForPayment(null);
      setPaymentForm({ amount: "" });
      showToast.success(t("netWorth.paymentRecorded"));
      fetchData();
    } catch (error) {
      console.error("Failed to record payment:", error);
      showToast.error(error instanceof Error ? error.message : t("common.error"));
    }
  };

  const handleDeleteAsset = async (id: string) => {
    if (!confirm(t("netWorth.deleteAssetConfirm"))) return;
    try {
      await api.delete(`/assets/${id}`);
      showToast.success(t("netWorth.assetDeleted"));
      // If this was the last item on the page and not page 1, go to previous page
      if (paginatedAssets.length === 1 && assetPage > 1) {
        setAssetPage(assetPage - 1);
      } else {
        fetchData();
      }
    } catch (error) {
      console.error("Failed to delete asset:", error);
      showToast.error(t("common.error"));
    }
  };

  const handleDeleteDebt = async (id: string) => {
    if (!confirm(t("netWorth.deleteDebtConfirm"))) return;
    try {
      await api.delete(`/debts/${id}`);
      showToast.success(t("netWorth.debtDeleted"));
      // If this was the last item on the page and not page 1, go to previous page
      if (paginatedDebts.length === 1 && debtPage > 1) {
        setDebtPage(debtPage - 1);
      } else {
        fetchData();
      }
    } catch (error) {
      console.error("Failed to delete debt:", error);
      showToast.error(t("common.error"));
    }
  };

  const handleEditAsset = (asset: Asset) => {
    setEditingAsset(asset);
    setAssetForm({
      category: asset.category,
      value: asset.value.toString(),
      date: new Date(asset.date).toISOString().split("T")[0],
    });
    setShowAssetForm(true);
  };

  const handleEditDebt = (debt: Debt) => {
    setEditingDebt(debt);
    const interestRate = debt.interestRate === null || debt.interestRate === undefined 
      ? "" 
      : String(debt.interestRate);
    const dueDate = debt.dueDate 
      ? new Date(debt.dueDate).toISOString().split("T")[0] ?? ""
      : "";
    setDebtForm({
      category: debt.category,
      amount: debt.amount.toString(),
      interestRate,
      dueDate,
    });
    setShowDebtForm(true);
  };

  const handleRecordPayment = (debt: Debt) => {
    setSelectedDebtForPayment(debt);
    setPaymentForm({ amount: "" });
    setShowPaymentForm(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString(language === "id" ? "id-ID" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const totalAssets = assets.reduce((sum, asset) => sum + asset.value, 0);
  const totalDebts = debts.reduce((sum, debt) => sum + debt.amount, 0);
  const netWorth = totalAssets - totalDebts;


  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-9 w-48 mb-2" />
          <Skeleton className="h-5 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Net Worth - Personal Finance App"
        description="Track your net worth by managing your assets and debts. View your financial position and monitor your wealth over time."
        keywords="net worth, assets, debts, wealth tracking, financial position"
        canonicalUrl="/net-worth"
      />
      <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("netWorth.title")}</h1>
        <p className="text-muted-foreground">
          {t("netWorth.subtitle")}
        </p>
      </div>

      {/* Net Worth Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <TrendingUp className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">{t("netWorth.totalAssets")}</p>
                <p className="text-lg md:text-xl font-bold text-primary tabular-nums font-mono truncate">{formatCurrency(totalAssets)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-4 w-4 text-destructive" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">{t("netWorth.totalDebts")}</p>
                <p className="text-lg md:text-xl font-bold text-destructive tabular-nums font-mono truncate">{formatCurrency(totalDebts)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-2 md:col-span-1">
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-chart-3/10 flex items-center justify-center shrink-0">
                <Scale className="h-4 w-4 text-chart-3" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">{t("dashboard.netWorth")}</p>
                <p className={`text-lg md:text-xl font-bold tabular-nums font-mono truncate ${netWorth >= 0 ? "text-primary" : "text-destructive"}`}>
                  {formatCurrency(netWorth)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>


      <div className="grid gap-6 md:grid-cols-2">
        {/* Assets Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{t("netWorth.assets")}</CardTitle>
            <Button
              size="sm"
              onClick={() => {
                setEditingAsset(null);
                setAssetForm({ category: "", value: "", date: new Date().toISOString().split("T")[0] });
                setShowAssetForm(!showAssetForm);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("netWorth.addAsset")}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {showAssetForm && (
              <form onSubmit={handleAssetSubmit} className="space-y-3 p-4 border rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="asset-category">{t("budget.category")}</Label>
                  <Input
                    id="asset-category"
                    value={assetForm.category}
                    onChange={(e) => setAssetForm({ ...assetForm, category: e.target.value })}
                    placeholder="e.g., Savings, Investment, Property"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="asset-value">{t("budget.amount")}</Label>
                  <Input
                    id="asset-value"
                    type="number"
                    step="0.01"
                    value={assetForm.value}
                    onChange={(e) => setAssetForm({ ...assetForm, value: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="asset-date">{t("budget.date")}</Label>
                  <Input
                    id="asset-date"
                    type="date"
                    value={assetForm.date}
                    onChange={(e) => setAssetForm({ ...assetForm, date: e.target.value })}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" size="sm">
                    {editingAsset ? t("common.update") : t("common.add")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowAssetForm(false);
                      setEditingAsset(null);
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                </div>
              </form>
            )}

            <div className="space-y-2">
              {assets.length === 0 ? (
                <div className="text-center py-8">
                  <TrendingUp className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">{t("netWorth.noAssets")}</p>
                </div>
              ) : (
                <>
                  {paginatedAssets.map((asset) => (
                    <div
                      key={asset.id}
                      className="flex items-center justify-between p-3 md:p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <TrendingUp className="h-4 w-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{asset.category}</p>
                          <p className="text-xs md:text-sm text-muted-foreground">{formatDate(asset.date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 md:gap-2 shrink-0">
                        <span className="font-medium tabular-nums font-mono text-sm md:text-base">{formatCurrency(asset.value)}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditAsset(asset)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteAsset(asset.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Asset Pagination */}
                  {assetTotalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="text-sm text-muted-foreground">
                        {t("budget.showing")} {(assetPage - 1) * itemsPerPage + 1} {t("budget.to")}{" "}
                        {Math.min(assetPage * itemsPerPage, assets.length)} {t("budget.of")}{" "}
                        {assets.length} {t("budget.entries")}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAssetPage((prev) => Math.max(1, prev - 1))}
                          disabled={assetPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          {t("budget.previous")}
                        </Button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, assetTotalPages) }, (_, i) => {
                            let pageNum: number;
                            if (assetTotalPages <= 5) {
                              pageNum = i + 1;
                            } else if (assetPage <= 3) {
                              pageNum = i + 1;
                            } else if (assetPage >= assetTotalPages - 2) {
                              pageNum = assetTotalPages - 4 + i;
                            } else {
                              pageNum = assetPage - 2 + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                variant={assetPage === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => setAssetPage(pageNum)}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAssetPage((prev) => Math.min(assetTotalPages, prev + 1))}
                          disabled={assetPage === assetTotalPages}
                        >
                          {t("budget.next")}
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Debts Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{t("netWorth.debts")}</CardTitle>
            <Button
              size="sm"
              onClick={() => {
                setEditingDebt(null);
                setDebtForm({ category: "", amount: "", interestRate: "", dueDate: "" });
                setShowDebtForm(!showDebtForm);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("netWorth.addDebt")}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {showDebtForm && (
              <form onSubmit={handleDebtSubmit} className="space-y-3 p-4 border rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="debt-category">{t("budget.category")}</Label>
                  <Input
                    id="debt-category"
                    value={debtForm.category}
                    onChange={(e) => setDebtForm({ ...debtForm, category: e.target.value })}
                    placeholder="e.g., Credit Card, Loan, Mortgage"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="debt-amount">{t("budget.amount")}</Label>
                  <Input
                    id="debt-amount"
                    type="number"
                    step="0.01"
                    value={debtForm.amount}
                    onChange={(e) => setDebtForm({ ...debtForm, amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="debt-interest">{t("netWorth.interestRate")}</Label>
                  <Input
                    id="debt-interest"
                    type="number"
                    step="0.01"
                    value={debtForm.interestRate}
                    onChange={(e) => setDebtForm({ ...debtForm, interestRate: e.target.value })}
                    placeholder={t("netWorth.optional")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="debt-due-date">{t("netWorth.dueDate")}</Label>
                  <Input
                    id="debt-due-date"
                    type="date"
                    value={debtForm.dueDate}
                    onChange={(e) => setDebtForm({ ...debtForm, dueDate: e.target.value })}
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" size="sm">
                    {editingDebt ? t("common.update") : t("common.add")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowDebtForm(false);
                      setEditingDebt(null);
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                </div>
              </form>
            )}

            {showPaymentForm && selectedDebtForPayment && (
              <form onSubmit={handlePaymentSubmit} className="space-y-3 p-4 border rounded-lg bg-muted">
                <div className="space-y-2">
                  <Label htmlFor="payment-amount">{t("netWorth.paymentAmount")}</Label>
                  <Input
                    id="payment-amount"
                    type="number"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("netWorth.debts")}: {formatCurrency(selectedDebtForPayment.amount)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" size="sm">
                    {t("netWorth.recordPayment")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowPaymentForm(false);
                      setSelectedDebtForPayment(null);
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                </div>
              </form>
            )}

            <div className="space-y-2">
              {debts.length === 0 ? (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-1">{t("netWorth.noDebts")}</p>
                  <p className="text-xs text-muted-foreground">{t("netWorth.addDebt")}</p>
                </div>
              ) : (
                <>
                  {paginatedDebts.map((debt) => (
                    <div
                      key={debt.id}
                      className="flex items-center justify-between p-3 md:p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                        <div className="h-8 w-8 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                          <AlertTriangle className="h-4 w-4 text-destructive" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{debt.category}</p>
                          <div className="text-xs md:text-sm text-muted-foreground space-y-0.5">
                            {debt.interestRate && <p>{t("netWorth.interestRate")}: {debt.interestRate}%</p>}
                            <p>{t("netWorth.dueDate")}: {formatDate(debt.dueDate)}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 md:gap-2 shrink-0">
                        <span className="font-medium tabular-nums font-mono text-sm md:text-base">{formatCurrency(debt.amount)}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRecordPayment(debt)}
                          title={t("netWorth.recordPayment")}
                        >
                          <DollarSign className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditDebt(debt)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteDebt(debt.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Debt Pagination */}
                  {debts.length > 0 && (
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="text-sm text-muted-foreground">
                        {t("budget.showing")} {(debtPage - 1) * itemsPerPage + 1} {t("budget.to")}{" "}
                        {Math.min(debtPage * itemsPerPage, debts.length)} {t("budget.of")}{" "}
                        {debts.length} {t("budget.entries")}
                      </div>
                      {debtTotalPages > 1 && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDebtPage((prev) => Math.max(1, prev - 1))}
                            disabled={debtPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            {t("budget.previous")}
                          </Button>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, debtTotalPages) }, (_, i) => {
                              let pageNum: number;
                              if (debtTotalPages <= 5) {
                                pageNum = i + 1;
                              } else if (debtPage <= 3) {
                                pageNum = i + 1;
                              } else if (debtPage >= debtTotalPages - 2) {
                                pageNum = debtTotalPages - 4 + i;
                              } else {
                                pageNum = debtPage - 2 + i;
                              }
                              return (
                                <Button
                                  key={pageNum}
                                  variant={debtPage === pageNum ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setDebtPage(pageNum)}
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDebtPage((prev) => Math.min(debtTotalPages, prev + 1))}
                            disabled={debtPage === debtTotalPages}
                          >
                            {t("budget.next")}
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
}
